import React, { useEffect, useRef } from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';

import { getSmartDateLabel } from "../../../../../utils/date";
import { ImageMessage } from './ImageMessage';

const Wrapper = styled(Box)`
    background: #1c2a34;
    padding: 5px;
    max-width: 60%;
    width: fit-content;
    display: flex;
    border-radius: 10px;
    word-break: break-word;
`;

const Own = styled(Box)`
    background: #075e54;
    padding: 5px;
    max-width: 60%;
    width: fit-content;
    margin-left: auto;
    display: flex;
    border-radius: 10px;
    word-break: break-word;
`;

const Text = styled(Typography)`
    font-size: 14px;
    padding: 0 25px 0 5px;
`;

const Time = styled(Typography)`
    font-size: 9px;
    color: #919191;
    position: absolute;
    right: 5px;
    bottom: 0px;
    margin-top: auto;
    word-break: keep-all;
`;

export const Messages = ({ messages }) => {
    const bottomRef = useRef(null);

    useEffect(() => {
    const timeout = setTimeout(() => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, 2000);

    return () => clearTimeout(timeout);
}, [messages]);


    return (
        <Box
            sx={{
                flexGrow: 1,
                backgroundColor: '#0b141a',
                backgroundSize: 'cover',
                overflowY: 'auto',
                width: '100%',
                paddingTop: '10px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {messages && messages.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <Box
                        sx={{
                            backgroundColor: '#1f2c33',
                            color: '#aebac1',
                            px: 2,
                            py: 0.5,
                            fontSize: '12px',
                            borderRadius: 1,
                            fontWeight: 500,
                        }}
                    >
                        No hay mensajes aún
                    </Box>
                </Box>
            )}

            {messages && messages.map((message, index) => {
                const showDate = index === 0;

                return (
                    <React.Fragment key={index}>
                        {showDate && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                                <Box
                                    sx={{
                                        backgroundColor: '#1f2c33',
                                        color: '#aebac1',
                                        px: 2,
                                        py: 0.5,
                                        fontSize: '12px',
                                        borderRadius: 1,
                                        fontWeight: 500,
                                    }}
                                >
                                    {getSmartDateLabel(message.date)}
                                </Box>
                            </Box>
                        )}

                        <Box sx={{ padding: '2px 20px', marginBottom: '2px' }}>
                            <Message message={message} />
                        </Box>
                    </React.Fragment>
                );
            })}

            <div ref={bottomRef} />
        </Box>
    );
};

const Message = ({ message }) => {
    const content = message.type === 'text' ? (
        <TextMessage message={message} />
    ) : message.type === 'sticker' ? (
        <img src={message.url} alt="sticker" style={{ width: 80, height: 80 }} />
    ) : message.type === 'template' ? (
        <TemplateTextMessage message={message} />
    ) : (
        <ImageMessage message={message} />
    );

    return message.fromMe ? <Own>{content}</Own> : <Wrapper>{content}</Wrapper>;
};

const TextMessage = ({ message }) => {
    return (
        <Box
            sx={{
                position: 'relative',
                paddingRight: '40px',
                minWidth: '50px',
            }}
        >
            <Text>{message.text}</Text>
            <Time>{message.time}</Time>
        </Box>
    );
};

const TemplateTextMessage = ({ message }) => {
    const { header, body, footer, buttons } = message.text;
    return (
        <Box
            sx={{
                position: 'relative',
                paddingRight: '50px',
                padding: '5px',
                minWidth: '50px',
            }}
        >
            {header && (
                <Typography sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '14px' }}>
                    {header}
                </Typography>
            )}
            {body && (
                <Typography sx={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                    {body}
                </Typography>
            )}
            {footer && (
                <Typography sx={{ fontSize: '12px', mt: 1, color: '#aebac1' }}>
                    {footer}
                </Typography>
            )}
            {Array.isArray(buttons) && buttons.length > 0 && (
                <Box
                    sx={{
                        mt: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        pt: 1,
                    }}
                >
                    {buttons.map((btn, idx) => {
                        const commonStyles = {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            color: '#53bdeb',
                            fontSize: '14px',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '8px',
                            '&:hover': {
                                textDecoration: 'underline',
                                cursor: 'pointer',
                            },
                        };

                        if (btn.type === 'URL' && btn.url) {
                            return (
                                <Link
                                    key={idx}
                                    href={btn.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="none"
                                    sx={commonStyles}
                                >
                                    <OpenInNewIcon fontSize="small" sx={{ color: '#53bdeb' }} />
                                    {btn.text || 'Abrir'}
                                </Link>
                            );
                        }

                        if (btn.type === 'QUICK_REPLY') {
                            return (
                                <Box key={idx} sx={commonStyles}>
                                    {btn.text || 'Responder'}
                                </Box>
                            );
                        }

                        return null;
                    })}
                </Box>
            )}
            <Time>{message.time}</Time>
        </Box>
    );
};
